import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCwIcon, TriangleAlertIcon } from 'lucide-react';

import {
  ApiError, api,
  type BatchRescoreRow, type BatchRescoreSummary, type ModuleRow,
} from '@/lib/api';
import { useContent, useNumber, useT, useTn } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { useCannotBecause } from '@/lib/permissions';
import { useDimensionLabel } from '@/lib/i18n';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * BATCH RE-SCORE — the capability that had an endpoint and no way in.
 *
 * `POST /api/assessments/rescore/batch` has been complete since PROGRESS §15:
 * a filter, a `MAX_BATCH` ceiling, one small transaction per instance, and a
 * dry-run that runs the real scoring path without writing. Nothing in the
 * console referenced it for an entire workstream, so the operation the plan
 * called *"applying a fix means a cohort"* could only be performed with `curl`.
 *
 * ### The dry-run is not a preference, it is the flow
 *
 * `rescore.service.ts` states the reason in one line worth repeating: *"a batch
 * that reports '412 results would change' is a decision; one that reports '412
 * results changed' is an incident."* So this screen cannot write until it has
 * previewed, exactly as the config editor cannot save until it has previewed —
 * the same rule for the same reason, and the two are the only places in the
 * console where one click can change hundreds of stored records.
 *
 * Changing the filter clears the preview. A preview of one cohort must not
 * authorise the re-scoring of another.
 *
 * ### Nothing is overwritten either way
 *
 * G5 is append-only: every superseded result is retained and reachable through
 * its `supersedes` chain. A batch re-score is therefore reversible by
 * inspection, which is the only sense in which it is reversible at all — and
 * that is worth saying on screen, because "re-score 412 runs" reads as
 * destructive and is not.
 */

function DeltaRow({ row }: { row: BatchRescoreRow }) {
  const t = useT();
  const number = useNumber();
  const dimensionLabel = useDimensionLabel();

  return (
    <li className="space-y-1 border-s-2 ps-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono" dir="ltr">{row.assessmentId}</span>
        {row.outcome === 'failed' ? (
          <Badge variant="destructive">{t('rescore.outcome.failed')}</Badge>
        ) : (
          <Badge variant={row.outcome === 'changed' ? 'default' : 'secondary'}>
            {t(`rescore.outcome.${row.outcome}`)}
          </Badge>
        )}
        {/* The QC movement, which is the fact that changes the decision. Named
            in both directions: a re-score can lift a hold as well as apply one. */}
        {row.qcState && (
          <Badge variant={row.qcState.to === 'flagged' ? 'destructive' : 'secondary'}>
            {t(row.qcState.to === 'flagged' ? 'rescore.willHold' : 'rescore.willRelease')}
          </Badge>
        )}
      </div>

      {row.error && <p className="text-destructive">{row.error}</p>}

      {row.headline && (
        <p className="text-muted-foreground">
          {row.headline.from} → {row.headline.to}
        </p>
      )}

      {/* Largest movement first, as the server sorts it. Capped at three: the
          point of a preview row is the SHAPE of the change, and a reader
          scanning four hundred rows does not read the fourth dimension of any
          of them. The full envelope is on the result once written. */}
      {row.dimensionDeltas && row.dimensionDeltas.length > 0 && (
        <p className="text-muted-foreground" dir="ltr">
          {row.dimensionDeltas.slice(0, 3).map((d) => (
            <span key={d.key} className="me-3">
              {dimensionLabel(d.key, { en: d.key, ar: d.key })}{' '}
              {number(d.from)}→{number(d.to)}
            </span>
          ))}
        </p>
      )}
    </li>
  );
}

function SummaryReport({ summary }: { summary: BatchRescoreSummary }) {
  const t = useT();
  const tn = useTn();
  const number = useNumber();

  const shown = summary.rows.filter((r) => r.outcome !== 'unchanged');

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {([
          ['rescore.stat.examined', summary.examined],
          ['rescore.stat.changed', summary.changed],
          ['rescore.stat.unchanged', summary.unchanged],
          ['rescore.stat.failed', summary.failed],
        ] as const).map(([key, value]) => (
          <div key={key} className="rounded-md border p-2 text-center">
            <p className="text-xl font-semibold" dir="ltr">{number(value)}</p>
            <p className="text-muted-foreground text-xs">{t(key)}</p>
          </div>
        ))}
      </div>

      {/* NOT one of the four tiles: a hold is a different kind of fact from a
          count of changes, and burying it in a row of numbers is how forty runs
          enter the QC queue without the person who sent them there noticing. */}
      {summary.held > 0 && (
        <Alert variant="destructive" role="alert">
          <TriangleAlertIcon className="size-4" />
          <AlertDescription>{tn('rescore.held', summary.held)}</AlertDescription>
        </Alert>
      )}
      {summary.unheld > 0 && (
        <Alert>
          <AlertDescription>{tn('rescore.unheld', summary.unheld)}</AlertDescription>
        </Alert>
      )}

      {/* The server reports truncation rather than silently capping, so the
          console must not silently drop the report. */}
      {summary.truncated && (
        <Alert>
          <AlertDescription>
            {t('rescore.truncated', { matched: number(summary.matched), examined: number(summary.examined) })}
          </AlertDescription>
        </Alert>
      )}

      {shown.length > 0 && (
        <div className="max-h-56 space-y-2 overflow-y-auto pe-1">
          <p className="text-muted-foreground text-xs">{t('rescore.rowsHint')}</p>
          <ul className="space-y-2">
            {shown.map((row) => (
              <DeltaRow key={row.assessmentId} row={row} />
            ))}
          </ul>
        </div>
      )}

      {summary.examined === 0 && (
        <Alert>
          <AlertDescription>{t('rescore.noMatches')}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

export function BatchRescoreDialog({
  modules,
  initialModuleId,
  onClose,
}: {
  modules: ModuleRow[];
  initialModuleId?: string;
  onClose: () => void;
}) {
  const t = useT();
  const content = useContent();
  const queryClient = useQueryClient();
  // Re-scoring writes results. An analyst reading a report may not, and finding
  // that out by pressing the button is not a design.
  const cannot = useCannotBecause('assessments:run');

  const [moduleId, setModuleId] = useState(initialModuleId ?? '');
  const [personaId, setPersonaId] = useState('');
  const [preview, setPreview] = useState<BatchRescoreSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const personas = useQuery({ queryKey: ['personas'], queryFn: api.personas });

  /** Whatever is selected, with the empty string meaning "not filtered on". */
  const filter = {
    ...(moduleId ? { moduleId } : {}),
    ...(personaId ? { personaId } : {}),
  };

  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : t('rescore.failed'));

  const dryRun = useMutation({
    mutationFn: () => api.batchRescore({ ...filter, dryRun: true }),
    onSuccess: (summary) => {
      setError(null);
      setPreview(summary);
    },
    onError: (err) => {
      setPreview(null);
      fail(err);
    },
  });

  const apply = useMutation({
    mutationFn: () => api.batchRescore({ ...filter, dryRun: false }),
    onSuccess: (summary) => {
      notify.success(
        t('rescore.done'),
        t('rescore.done.detail', { changed: summary.changed, examined: summary.examined }),
      );
      // Results and the QC queue both move; the badge in the sidebar counts the
      // latter and would otherwise go on showing the pre-batch number.
      void queryClient.invalidateQueries({ queryKey: ['results'] });
      void queryClient.invalidateQueries({ queryKey: ['qc-queue'] });
      onClose();
    },
    onError: fail,
  });

  /** Any change to the filter invalidates the preview it was not computed from. */
  const refilter = (next: () => void) => {
    next();
    setPreview(null);
  };

  return (
    <DialogContent className="sm:max-w-3xl">
      <DialogHeader>
        <DialogTitle>{t('rescore.title')}</DialogTitle>
        <DialogDescription>{t('rescore.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="rescore-module">{t('rescore.module')}</Label>
          <Select
            value={moduleId}
            onValueChange={(next) => refilter(() => setModuleId(next))}
          >
            <SelectTrigger id="rescore-module" aria-label={t('rescore.module')}>
              <SelectValue placeholder={t('rescore.allModules')} />
            </SelectTrigger>
            <SelectContent>
              {modules.map((module) => (
                <SelectItem key={module.moduleId} value={module.moduleId}>
                  {content(module.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="rescore-persona">{t('rescore.persona')}</Label>
          <Select
            value={personaId}
            onValueChange={(next) => refilter(() => setPersonaId(next))}
          >
            <SelectTrigger id="rescore-persona" aria-label={t('rescore.persona')}>
              <SelectValue placeholder={t('rescore.allPersonas')} />
            </SelectTrigger>
            <SelectContent>
              {(personas.data?.items ?? []).map((persona) => (
                <SelectItem key={persona.personaId} value={persona.personaId}>
                  {content(persona.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Said before anything is pressed, not in a toast afterwards: "re-score
          412 runs" reads as destructive and is not. */}
      <p className="text-muted-foreground text-xs">{t('rescore.appendOnly')}</p>

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {preview && <SummaryReport summary={preview} />}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="outline"
          onClick={() => dryRun.mutate()}
          disabled={dryRun.isPending || apply.isPending}
        >
          <RefreshCwIcon className="size-4" />
          {dryRun.isPending ? t('rescore.checking') : t('rescore.dryRun')}
        </Button>
        <Button
          onClick={() => apply.mutate()}
          disabled={
            apply.isPending
            || cannot !== null
            // Gated on a preview, and on the preview having found something to
            // do. Re-scoring a cohort where nothing would change writes a new
            // result for every run and changes not one score.
            || preview === null
            || preview.changed === 0
          }
          title={cannot ?? (preview === null ? t('rescore.dryRunFirst') : undefined)}
        >
          {apply.isPending
            ? t('rescore.applying')
            : t('rescore.apply', { count: preview?.changed ?? 0 })}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
